import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, cn } from "../../../components/ui";
import { exchangeApi, type ExchangeNotification } from "./api";
import { relative } from "./format";
import { CM_BASE } from "./components";

// Avisos del Cargo Market (publicaciones, coincidencias, ofertas, viajes,
// documentos). Cada uno lleva a la pantalla donde se actúa, según el papel de
// la compañía en él (`role`, que el API guarda en el aviso). Los mismos avisos
// llegan además a la bandeja general, por push y, si piden acción, por correo.

const ICONS: Record<string, string> = {
  load_published: "ri-add-box-line",
  load_match: "ri-radar-line",
  capacity_published: "ri-roadster-line",
  capacity_match: "ri-route-line",
  load_invite: "ri-mail-star-line",
  load_cancelled: "ri-forbid-line",
  load_expired: "ri-calendar-close-line",
  offer_received: "ri-price-tag-3-line",
  offer_updated: "ri-price-tag-3-line",
  offer_withdrawn: "ri-arrow-go-back-line",
  offer_rejected: "ri-close-circle-line",
  offers_closing: "ri-timer-line",
  trip_awarded: "ri-trophy-line",
  trip_assign_reminder: "ri-alarm-line",
  trip_confirmed: "ri-checkbox-circle-line",
  trip_to_pickup: "ri-navigation-line",
  trip_at_pickup: "ri-map-pin-2-line",
  trip_loaded: "ri-inbox-archive-line",
  trip_in_transit: "ri-truck-line",
  trip_at_dropoff: "ri-map-pin-range-line",
  trip_delivered: "ri-hand-heart-line",
  trip_closed: "ri-flag-2-line",
  trip_cancelled: "ri-close-circle-line",
  trip_incident: "ri-alarm-warning-line",
  trip_rated: "ri-star-line",
  document_approved: "ri-shield-check-line",
  document_rejected: "ri-shield-cross-line",
  document_expiring: "ri-time-line",
  document_expired: "ri-error-warning-line",
};
/** Cada cuánto se refresca la campana (los avisos también llegan por push). */
const POLL_MS = 60_000;

/** Pantalla del aviso: por su papel; los avisos viejos (sin `role`) se resuelven como antes. */
export function notificationPath(n: ExchangeNotification): string | null {
  const space = n.role === "shipper" ? "embarcador" : n.role === "carrier" ? "transportista" : null;
  if (n.ref?.tripId && space) return `${CM_BASE}/${space}/viajes?id=${n.ref.tripId}`;
  if (n.type.startsWith("document")) return `${CM_BASE}/${space || "transportista"}/verificacion`;
  if (n.ref?.loadId) {
    const asShipper = n.role ? n.role === "shipper" : ["offer_received", "offer_updated", "offer_withdrawn", "offers_closing", "load_expired", "load_published", "capacity_match"].includes(n.type);
    return asShipper ? `${CM_BASE}/embarcador/cargas?id=${n.ref.loadId}` : `${CM_BASE}/transportista/bolsa`;
  }
  if (n.type === "capacity_published") return `${CM_BASE}/transportista/capacidad`;
  return null;
}

export function NotificationsBell({ onUnreadChange }: { onUnreadChange?: (n: number) => void }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ExchangeNotification[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const unread = items.filter((n) => !n.read).length;

  useEffect(() => {
    let alive = true;
    const load = () => exchangeApi.notifications().then((list) => alive && setItems(list)).catch(() => undefined);
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);
  useEffect(() => onUnreadChange?.(unread), [unread, onUnreadChange]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const go = async (n: ExchangeNotification) => {
    setOpen(false);
    const path = notificationPath(n);
    if (path) {
      navigate(path);
    } else if (n.ref?.tripId) {
      try {
        const trip = await exchangeApi.trip(n.ref.tripId);
        navigate(`${CM_BASE}/${trip.role === "carrier" ? "transportista" : "embarcador"}/viajes?id=${trip.id}`);
      } catch {
        navigate(`${CM_BASE}/transportista/viajes`);
      }
    }
  };

  const markRead = async () => {
    await exchangeApi.readNotifications().catch(() => undefined);
    setItems((list) => list.map((n) => ({ ...n, read: true })));
  };

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="sm" iconOnly aria-label="Avisos de Cargo Market" onClick={() => setOpen((o) => !o)} icon={<i className="ri-notification-3-line text-lg" />} />
      {unread > 0 && <span className="pointer-events-none absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-error text-white text-[10px] leading-4 text-center">{unread}</span>}
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] z-[60] rounded-xl border border-line bg-surface shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <p className="text-sm font-semibold text-content">Avisos de Cargo Market</p>
            {unread > 0 && <Button size="xs" variant="link" onClick={markRead}>Marcar leídos</Button>}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {items.length === 0 && <li className="px-4 py-6 text-center text-sm text-content-muted">Sin avisos</li>}
            {items.map((n) => (
              <li key={n.id}>
                <button type="button" onClick={() => void go(n)} className={cn("w-full text-left px-4 py-3 flex gap-3 hover:bg-subtle border-b border-line last:border-0", !n.read && "bg-primary-500/5")}>
                  <i className={cn(ICONS[n.type] || "ri-information-line", "text-lg text-primary-500 mt-0.5")} />
                  <span className="min-w-0">
                    <span className={cn("block text-sm text-content", !n.read && "font-semibold")}>{n.title}</span>
                    {n.body && <span className="block text-xs text-content-muted truncate">{n.body}</span>}
                    <span className="block text-[11px] text-content-faint mt-0.5">{relative(n.createdAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
